import { useState } from "react";
import { Download, Eye, Inbox, Paperclip, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import {
  useDownloadMailboxAttachment,
  useMailbox,
  useMailboxMessage,
  useSendFromBox,
} from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Field, Input, Textarea } from "@/components/ui/Input";
import { EmptyState, TBody, TD, TH, THead, TR, Table } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { Badge } from "@/components/ui/Badge";
import { formatDate, saveBlob } from "@/lib/utils";
import type { Subscriber } from "@/types";

export function MailboxTab({ subscriber }: { subscriber: Subscriber }) {
  const subscriberId = subscriber.id;
  const list = useMailbox(subscriberId);
  const [composeOpen, setComposeOpen] = useState(false);
  const [openMsgId, setOpenMsgId] = useState<string | null>(null);

  // Display only. The server (buildBoxAddress in @pahcertificar/certmanager-mailbox)
  // is authoritative for the address used to send from + receive into this box.
  const boxAddress = `${subscriber.documentType}${subscriber.documentNumber}@pahcertificar.com`;

  const messages = list.data?.data ?? [];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <CardTitle>Buzón</CardTitle>
          <p className="mt-0.5 truncate font-mono text-xs text-slate-500">{boxAddress}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => list.refetch()} aria-label="Refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" onClick={() => setComposeOpen(true)}>
            <Send className="h-3.5 w-3.5" /> Redactar
          </Button>
        </div>
      </CardHeader>

      {list.isPending ? (
        <CenteredSpinner />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : messages.length === 0 ? (
        <EmptyState
          title="Buzón vacío"
          description={`Aún no hay correos. Las notificaciones de Andes y de otras integraciones enviadas a ${boxAddress} aparecerán aquí.`}
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Tipo</TH>
              <TH>De / Para</TH>
              <TH>Asunto</TH>
              <TH>Fecha</TH>
              <TH className="text-right">Acciones</TH>
            </tr>
          </THead>
          <TBody>
            {messages.map((m) => (
              <TR key={m.id}>
                <TD>
                  {m.direction === "inbound" ? (
                    <Badge tone="info">
                      <Inbox className="h-3 w-3" /> recibido
                    </Badge>
                  ) : (
                    <Badge tone="brand">
                      <Send className="h-3 w-3" /> enviado
                    </Badge>
                  )}
                </TD>
                <TD className="text-xs text-slate-900">
                  {m.direction === "inbound" ? m.fromAddress : m.toAddress}
                </TD>
                <TD>
                  <p className="max-w-[28ch] truncate text-sm text-slate-900">
                    {m.subject || "(sin asunto)"}
                  </p>
                  {m.snippet && (
                    <p className="max-w-[44ch] truncate text-xs text-slate-500">{m.snippet}</p>
                  )}
                </TD>
                <TD className="whitespace-nowrap text-[11px] text-slate-500">
                  {formatDate(m.receivedAt)}
                </TD>
                <TD className="text-right">
                  <div className="inline-flex items-center gap-1">
                    {m.hasAttachments && <Paperclip className="h-3.5 w-3.5 text-slate-400" />}
                    <Button size="sm" variant="ghost" onClick={() => setOpenMsgId(m.id)} aria-label="Abrir">
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {composeOpen && (
        <ComposeDialog
          subscriberId={subscriberId}
          boxAddress={boxAddress}
          defaultTo={subscriber.email}
          onClose={() => setComposeOpen(false)}
        />
      )}

      <ViewMessageDialog
        subscriberId={subscriberId}
        msgId={openMsgId}
        onClose={() => setOpenMsgId(null)}
      />
    </Card>
  );
}

// ── Compose / send from the box ─────────────────────────────────────────────

function ComposeDialog({
  subscriberId,
  boxAddress,
  defaultTo,
  onClose,
}: {
  subscriberId: string;
  boxAddress: string;
  defaultTo: string;
  onClose: () => void;
}) {
  const send = useSendFromBox(subscriberId);
  const [to, setTo] = useState(defaultTo ?? "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  return (
    <Dialog
      open
      onClose={onClose}
      title="Redactar correo"
      description={`Se enviará desde ${boxAddress}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            form="mailbox-compose-form"
            type="submit"
            loading={send.isPending}
            disabled={!to.trim() || !subject.trim() || !body.trim()}
          >
            <Send className="h-3.5 w-3.5" /> Enviar
          </Button>
        </>
      }
    >
      <form
        id="mailbox-compose-form"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await send.mutateAsync({ to: to.trim(), subject: subject.trim(), text: body });
            toast.success("Correo enviado");
            onClose();
          } catch (err) {
            if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
            else toast.error("No se pudo enviar el correo");
          }
        }}
        className="space-y-4"
      >
        <Field label="Para" required>
          <Input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            required
            placeholder="cliente@correo.com"
          />
        </Field>
        <Field label="Asunto" required>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            maxLength={255}
          />
        </Field>
        <Field label="Mensaje" required>
          <Textarea
            rows={10}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            maxLength={50_000}
          />
        </Field>
      </form>
    </Dialog>
  );
}

// ── Read a message ──────────────────────────────────────────────────────────

function ViewMessageDialog({
  subscriberId,
  msgId,
  onClose,
}: {
  subscriberId: string;
  msgId: string | null;
  onClose: () => void;
}) {
  const q = useMailboxMessage(subscriberId, msgId);
  const download = useDownloadMailboxAttachment(subscriberId);

  async function onDownload(index: number) {
    if (!msgId) return;
    try {
      const { blob, fileName } = await download.mutateAsync({ msgId, index });
      saveBlob(blob, fileName);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("No se pudo descargar el adjunto");
    }
  }

  return (
    <Dialog
      open={!!msgId}
      onClose={onClose}
      title={q.data?.message.subject || "Mensaje"}
      className="max-w-3xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {q.isPending ? (
        <CenteredSpinner />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.data ? (
        <div className="space-y-3">
          <div className="space-y-0.5 text-xs text-slate-600">
            <p>
              <span className="text-slate-400">De:</span> {q.data.message.fromAddress}
            </p>
            <p>
              <span className="text-slate-400">Para:</span> {q.data.message.toAddress}
            </p>
            <p>
              <span className="text-slate-400">Fecha:</span> {formatDate(q.data.message.receivedAt)}
            </p>
          </div>
          {q.data.attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 border-y border-slate-100 py-2">
              {q.data.attachments.map((a) => (
                <Button
                  key={a.index}
                  size="sm"
                  variant="outline"
                  loading={download.isPending && download.variables?.index === a.index}
                  onClick={() => onDownload(a.index)}
                >
                  <Download className="h-3.5 w-3.5" /> {a.filename}
                </Button>
              ))}
            </div>
          )}
          <MessageBody html={q.data.html} text={q.data.text} />
        </div>
      ) : null}
    </Dialog>
  );
}

// Untrusted inbound HTML is rendered inside a fully-sandboxed iframe (no scripts,
// no same-origin) so it can never execute in the admin origin.
function MessageBody({ html, text }: { html: string | null; text: string | null }) {
  if (html) {
    return (
      <iframe
        title="message-body"
        sandbox=""
        srcDoc={html}
        className="h-[50vh] w-full rounded-lg border border-slate-200 bg-white"
      />
    );
  }
  return (
    <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words text-sm text-slate-800">
      {text || "(sin contenido)"}
    </pre>
  );
}
